import { t } from '@/i18n/i18n'
import { ApplicationDataService } from '@/shared/service/applicationDataService'
import { ErrorService } from '@/shared/service/errorService'
import { FilesService } from '@/shared/service/files.service'
import { type PreviewGrabData } from '@/shared/types/applicationData.type'
import { create } from '@/shared/utils/zustandVue'
import { entries } from '@/utils'

import { SnackbarService } from '../../../shared/snackbar/snackbar.service'
import { useApplicationCfgStore } from '../../applicationCfg/applicationCfg.store'
import { StaticConfigService } from '../staticCfg.service'
import {
  type FontChangeSet,
  type Lang,
  LANGS,
  type StaticConfig,
} from '../staticCfg.types'

class StaticConfigState {
  config: StaticConfig | undefined
  dataSets: Record<Lang, PreviewGrabData | undefined> = {
    en: undefined,
    fr: undefined,
  }
}

type StaticConfigStore = StaticConfigState & {
  setConfig: (config: StaticConfig | undefined) => void
  setDataSets: (dataSets: Record<Lang, PreviewGrabData | undefined>) => void
  loadStaticConfig: (payload: { oid: string }) => Promise<void>
  createStaticConfig: () => void
  loadPreviewDataSets: () => Promise<void>
  uploadFonts: (payload: {
    config: StaticConfig
    changeset: FontChangeSet
  }) => Promise<void>
  saveStaticConfig: (staticCfg: StaticConfig) => Promise<void>
  reset: () => void
}

export const useStaticConfigStore = create<StaticConfigStore>((set, get) => ({
  ...new StaticConfigState(),
  setConfig: (config) => {
    set({ config })
  },
  setDataSets: (dataSets) => {
    set({ dataSets })
  },
  async loadStaticConfig({ oid }: { oid: string }) {
    try {
      const config = await StaticConfigService.get(oid)
      get().setConfig(config ?? undefined)
    } catch (e) {
      ErrorService.handleError(e)
    }
  },
  createStaticConfig() {
    const appCfg = useApplicationCfgStore.getState().applicationCfg
    if (!appCfg) return
    const config: StaticConfig = {
      colorAction: appCfg.colorAction,
      colorBlockAction: appCfg.colorAction,
      colorBackground: appCfg.colorBackground,
      colorTitle: appCfg.colorTitle,
      colorBlockBackground: appCfg.colorBlockBackground,
      colorBlockText: appCfg.colorBlockText,
      fontText: appCfg.fontTextRegular,
      fontTitle: appCfg.fontTitle,
      smartBannerEnabled: true,
      label: 'Nouveau widget',
      pages: [],
    }
    get().setConfig(config)
  },
  async loadPreviewDataSets() {
    try {
      const dataSets: Record<Lang, PreviewGrabData | undefined> = {
        en: undefined,
        fr: undefined,
      }
      await Promise.all(
        LANGS.map(async (lang: Lang): Promise<void> => {
          dataSets[lang] = await ApplicationDataService.getCurrent(lang, true)
        }),
      )
      get().setDataSets(dataSets)
    } catch (e) {
      ErrorService.handleError(e)
    }
  },
  async uploadFonts({
    config,
    changeset,
  }: {
    config: StaticConfig
    changeset: FontChangeSet
  }) {
    try {
      await Promise.all(
        entries(changeset)
          .filter(
            <U, T>(arg: [U, T | undefined]): arg is [U, T] => arg[1] != null,
          )
          .map(async ([key, value]) =>
            FilesService.uploadFont(value).then(
              ({ filename, postScriptName }) => {
                const configFont = config[key]
                if (!configFont) return
                configFont.id = filename
                configFont.postScriptName = postScriptName
              },
            ),
          ),
      )
    } catch (e) {
      ErrorService.handleError(e)
    }
  },
  async saveStaticConfig(staticCfg: StaticConfig) {
    try {
      const saved = await StaticConfigService.save(staticCfg)
      get().setConfig(saved)
      SnackbarService.info(t('GENERAL.SAVED'))
    } catch (e) {
      ErrorService.handleError(e)
    }
  },
  reset: () => {
    set({ ...new StaticConfigState() })
  },
}))
